import { useState, useCallback } from 'react';
import { MODES } from '../../../../utils/constant';
import { getAirUnit, getComfortRange, isSetPointsOverlapping } from '../../../../utils/utility';
import { getSliderMarksArray, stopThumbs, correctSetpointsForMode } from '../util';

export const useIQAirSlider = ( IQAirPoints, setIQAirPoints, IQAirComfortRange, setIQAirComfortRange, iqAirDetails, availableModes, setComfortRange, setSliderMarksArray, IQAirRange, setIqAirDetails ) => {
  const [range, setRange] = useState({ cool_setpoint: null, heat_setpoint: null });
  const [marksArray, setMarksArray] = useState({ cool_setpoint: null, heat_setpoint: null });
  const [selectedValue, setSelectedValue] = useState({ local: "" });

  const isF = getAirUnit() === "F";

  const updateSliderMarksAndRange = useCallback((updatedPoints) => {
    const sliderMarkArrCool = getSliderMarksArray(updatedPoints.cool_setpoint);
    const sliderMarkArrHeat = getSliderMarksArray(updatedPoints.heat_setpoint);

    // Update the main component's marks array state
    setSliderMarksArray({ heat_setpoint: sliderMarkArrHeat, cool_setpoint: sliderMarkArrCool });
    
    // Use getComfortRange function like the old code
    const rangeValCool = getComfortRange(updatedPoints.cool_setpoint, IQAirComfortRange.cool_comfort_range);
    const rangeValHeat = getComfortRange(updatedPoints.heat_setpoint, IQAirComfortRange.heat_comfort_range);
    
    // Update the main component's comfort range state
    setComfortRange({ heat_setpoint: rangeValHeat, cool_setpoint: rangeValCool });
  }, [IQAirComfortRange, setComfortRange, setSliderMarksArray]);

  const handleConfirmSelection = useCallback((type, value) => {
    const type2 = type === 'cool_setpoint' ? 'heat_setpoint' : 'cool_setpoint';
    const isSmartMode = selectedValue.local === 'Smart Mode';
    let setpointObj = { [type]: value };
    let markObj = {};
    let comfortRangeObj = {};

    // Check overlapping
    const overlap = isSetPointsOverlapping(IQAirPoints, type, value);
    
    if ([MODES.COOL, MODES.HEAT].includes(iqAirDetails.airMode)) {
      const adjustedValue = overlap.status ? IQAirPoints[type2] + overlap.adjust : IQAirPoints[type2];
      setpointObj[type2] = adjustedValue;

      // Update marks + comfort range for the other setpoint in SmartMode
      if (isSmartMode) {
        const sliderMarkArr = getSliderMarksArray(adjustedValue);
        const comfortRange = getComfortRange(adjustedValue, IQAirComfortRange[`cool_comfort_range`]);
        markObj[type2] = sliderMarkArr;
        comfortRangeObj[type2] = comfortRange;
      }
    }

    setIQAirPoints((prev) => ({ ...prev, ...setpointObj }));
    
    if (!isSmartMode) return;
    
    // Process marks + comfort range for the current type
    markObj[type] = getSliderMarksArray(value);
    comfortRangeObj[type] = getComfortRange(value, IQAirComfortRange[`cool_comfort_range`]);
    
    // Apply updates
    setMarksArray({ ...marksArray, ...markObj });
    setRange({ ...range, ...comfortRangeObj });
  }, [IQAirPoints, selectedValue.local, iqAirDetails.airMode, IQAirComfortRange, marksArray, range]);

  const validateAndApplyModeChange = useCallback((airMode) => {
    // Use proper range values from IQAirRange
    const min = IQAirRange?.min ;
    const max = IQAirRange?.max ;

    // First update mode
    setIqAirDetails((prev) => ({ ...prev, airMode }));

    setIQAirPoints((prev) => {
      // Guard (if values missing, do nothing)
      if (prev.heat_setpoint == null || prev.cool_setpoint == null) return prev;

      const correctedPoints = correctSetpointsForMode({
        airMode,
        heat: prev.heat_setpoint,
        cool: prev.cool_setpoint,
        min,
        max,
      });

      const updatedPoints = {
        ...prev,
        ...correctedPoints,
      };

      // Update slider marks and range after mode change
      setTimeout(() => {
        updateSliderMarksAndRange(updatedPoints);
      }, 0);

      return updatedPoints;
    });
  }, [setIQAirPoints, IQAirRange, setIqAirDetails, updateSliderMarksAndRange]);

  const handleComfortRangeChange = useCallback((setpointType, [left, right], index) => {
    const center = IQAirPoints[setpointType];
    const rangeDividend = getAirUnit() === "F" ? 0.5 : 1;
    
    if (stopThumbs(left, right, center, index, isF)) return;
    
    // Update internal range state
    setRange(prev => ({
      ...prev,
      [setpointType]: [left, right],
    }));
    
    // Update main component's comfortRange state
    setComfortRange(prev => ({
      ...prev,
      [setpointType]: [left, right],
    }));
    
    // Update IQAirComfortRange for persistence
    setIQAirComfortRange(prev => ({
      ...prev,
      [`${setpointType.split('_')[0]}_comfort_range`]: {
        lower: (center - left) * rangeDividend,
        upper: (right - center) * rangeDividend,
      }
    }));
  }, [IQAirPoints, isF, setIQAirComfortRange, setComfortRange]);

  return {
    range,
    setRange,
    marksArray,
    setMarksArray,
    selectedValue,
    setSelectedValue,
    handleConfirmSelection,
    validateAndApplyModeChange,
    handleComfortRangeChange,
    updateSliderMarksAndRange
  };
};
